/**
 * 展示层格式化。
 *
 * 设计稿里的数字都是紧凑写法（`8.0k`、`123 GB`、`2 小时前`），统一放在这里，
 * 避免同一种格式在多个组件里各写一遍最后不一致。
 */

/**
 * 紧凑计数：`8.0k` / `22.6k` / `1.2M`。
 *
 * 设计稿的 `8.0k` 保留一位小数（不是 `8k`），所以 1000 以上一律一位小数；
 * 1000 以下直接给整数，因为 `8.0` 这种写法在小数值上反而奇怪。
 */
export function formatCompactCount(value: number): string {
  if (!Number.isFinite(value) || value < 0) return '0'
  if (value < 1000) return String(Math.round(value))
  if (value < 1_000_000) return `${(value / 1000).toFixed(1)}k`
  if (value < 1_000_000_000) return `${(value / 1_000_000).toFixed(1)}M`
  return `${(value / 1_000_000_000).toFixed(1)}B`
}

const BYTE_UNITS = ['B', 'KB', 'MB', 'GB', 'TB', 'PB'] as const

/**
 * 字节数 → `123 GB`。
 *
 * 后端字节数一律整数（openapi.yaml 约定）。这里按 1024 进制，且 GB 以上不带
 * 小数——设计稿的「123 GB」「31.2 GB」说明大值取整、小值留一位，与之对齐。
 */
export function formatBytes(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes <= 0) return '0 B'
  const exponent = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), BYTE_UNITS.length - 1)
  const scaled = bytes / 1024 ** exponent
  const unit = BYTE_UNITS[exponent] ?? 'B'
  if (exponent === 0) return `${Math.round(scaled)} ${unit}`
  const decimals = scaled >= 100 ? 0 : 1
  return `${scaled.toFixed(decimals)} ${unit}`
}

/** RFC 3339 → `2026-08-02`。后端时间一律 RFC 3339。 */
export function formatDate(iso: string): string {
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return '—'
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

export function formatDateTime(iso: string): string {
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return '—'
  const hours = String(date.getHours()).padStart(2, '0')
  const minutes = String(date.getMinutes()).padStart(2, '0')
  return `${formatDate(iso)} ${hours}:${minutes}`
}

const MINUTE = 60_000
const HOUR = 60 * MINUTE
const DAY = 24 * HOUR

/** 相对时间：`刚刚` / `2 小时前` / `昨天` / `3 天前`，超过 30 天回落到日期。 */
export function formatRelativeTime(iso: string, now: number = Date.now()): string {
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return '—'
  const diff = now - date.getTime()
  if (diff < 0) return formatDate(iso)
  if (diff < MINUTE) return '刚刚'
  if (diff < HOUR) return `${Math.floor(diff / MINUTE)} 分钟前`
  if (diff < DAY) return `${Math.floor(diff / HOUR)} 小时前`
  if (diff < 2 * DAY) return '昨天'
  if (diff < 30 * DAY) return `${Math.floor(diff / DAY)} 天前`
  return formatDate(iso)
}

/** 秒 → `12:34`（下载页倒计时）。超过一小时补出小时段。 */
export function formatCountdown(totalSeconds: number): string {
  const clamped = Math.max(0, Math.floor(totalSeconds))
  const hours = Math.floor(clamped / 3600)
  const minutes = Math.floor((clamped % 3600) / 60)
  const seconds = clamped % 60
  const mm = String(minutes).padStart(2, '0')
  const ss = String(seconds).padStart(2, '0')
  return hours > 0 ? `${hours}:${mm}:${ss}` : `${mm}:${ss}`
}

/** 百分比整数，用于上传进度条。 */
export function formatPercent(ratio: number): string {
  return `${Math.round(Math.min(1, Math.max(0, ratio)) * 100)}%`
}
